import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { oauthService } from './services/oauth';
import { CalendarProvider } from './types';

export const CalendarOAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const handledRef = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      if (handledRef.current) return; // guard against double-invocation in Strict Mode
      handledRef.current = true;
      const code = searchParams.get('code');
      const errorParam = searchParams.get('error');
      const providerFromState = (() => {
        const raw = searchParams.get('state');
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed.provider === 'string')
              return parsed.provider as CalendarProvider;
          } catch (e) {
            // ignore malformed state
          }
        }
        const stored = sessionStorage.getItem('oauth_provider');
        return (stored as CalendarProvider) || undefined;
      })();

      if (errorParam) {
        setError(`OAuth error: ${errorParam}`);
        setStatus('error');
        return;
      }

      if (!code) {
        setError('No authorization code received');
        setStatus('error');
        return;
      }

      if (!providerFromState || !['google', 'outlook', 'apple'].includes(providerFromState)) {
        setError('Invalid or missing provider');
        setStatus('error');
        return;
      }

      try {
        setStatus('loading');
        await oauthService.callback(code, providerFromState);
        setStatus('success');
        // Clean the URL to avoid re-processing the same code on re-render
        const cleanUrl = `${window.location.origin}/calendars/oauth/callback`;
        window.history.replaceState({}, document.title, cleanUrl);
        sessionStorage.removeItem('oauth_provider');

        // Redirect to calendars page after successful connection
        setTimeout(() => {
          navigate('/calendars');
        }, 2000);
      } catch (err) {
        // If we've already marked success, ignore transient errors
        if (status !== 'success') {
          const errorMessage = err instanceof Error ? err.message : 'Failed to complete OAuth flow';
          setError(errorMessage);
          setStatus('error');
        }
      }
    };

    handleCallback();
  }, [searchParams, navigate, status]);

  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Completing authorization...</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-green-600 text-4xl mb-4">✓</div>
          <p className="text-gray-600 dark:text-gray-300 mb-4">Calendar connected successfully!</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">Redirecting to calendars...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-600 text-4xl mb-4">✗</div>
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button
            onClick={() => navigate('/calendars')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Return to Calendars
          </button>
        </div>
      </div>
    );
  }

  return null;
};
